// src/pages/ResetPasswordPage.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Mail,
  Lock,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Home,
  RefreshCw,
  KeyRound,
  Shield,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/logo.png";
import axios from "axios";
import { BASE_URL } from "../../hooks/useAuth";

/** ---- 안정형 Toast: open이 false -> true로 바뀔 때만 타이머 시작 ---- */
function Toast({ open, text, onDone }) {
  const prevOpenRef = useRef(false);
  const timerRef = useRef(null);
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    // 이미 돌아가는 타이머 정리
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // open이 true로 "전이"될 때만 타이머 시작
    if (open && !prevOpenRef.current) {
      timerRef.current = setTimeout(() => {
        onDoneRef.current?.();
      }, 3000);
    }

    prevOpenRef.current = open;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed top-6 right-6 z-50 animate-slide-in">
      <div className="bg-gray-900/95 backdrop-blur-xl border border-gray-800 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-2xl">
        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-white" />
        </div>
        <p className="text-white font-medium">{text}</p>
      </div>
    </div>
  );
}

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: 이메일입력, 2: 인증코드, 3: 새비밀번호
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // 타이머 관련
  const [timeLeft, setTimeLeft] = useState(1800); // 30분 = 1800초
  const [isTimerActive, setIsTimerActive] = useState(false);

  // 타이머 효과
  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsTimerActive(false);
      setError("인증 시간이 만료되었습니다. 다시 전송해주세요.");
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  // 시간 포맷팅
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // 이메일로 인증코드 전송
  const sendVerificationCode = async () => {
    setIsLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${BASE_URL}/email_verify_find_password_20250821`,
        { email }
      );

      if (data.status && data.not_found_email) {
        setError("등록되지 않은 이메일 주소입니다.");
        return;
      }

      if (data.status && data.auth_code) {
        // 서버에서 받은 인증코드 저장
        setVerificationCode(data.auth_code);
        setStep(2);
        setTimeLeft(1800);
        setIsTimerActive(true);
        setToastMessage("인증코드가 이메일로 발송되었습니다.");
        setShowToast(true);
      } else {
        setError("이메일 전송에 실패했습니다.");
      }
    } catch (err) {
      setError("이메일 전송 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 인증코드 재전송
  const resendCode = async () => {
    setInputCode("");
    await sendVerificationCode();
  };

  // 인증코드 확인
  const verifyCode = () => {
    if (inputCode === verificationCode) {
      setStep(3);
      setError("");
    } else {
      setError("인증코드가 일치하지 않습니다.");
    }
  };

  // 비밀번호 재설정
  const resetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setError("비밀번호가 일치하지 않습니다.");
      return;
    }

    if (newPassword.length < 8) {
      setError("비밀번호는 8자 이상이어야 합니다.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${BASE_URL}/reset_password_20250821`, {
        email,
        reset_password: newPassword,
      });

      if (data.status) {
        setToastMessage("비밀번호가 성공적으로 변경되었습니다!");
        setShowToast(true);
        setTimeout(() => nav("/login"), 1500);
      } else {
        setError("비밀번호 변경에 실패했습니다.");
      }
    } catch (err) {
      setError("비밀번호 변경 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Toast 닫기 콜백: 안정화(useCallback) ---
  const handleToastDone = useCallback(() => {
    setShowToast(false);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-[150px] opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-600 rounded-full filter blur-[150px] opacity-30 animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-600 rounded-full filter blur-[200px] opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
            }}
          ></div>
        </div>
      </div>

      <div className="relative z-10 min-h-screen flex">
        <button
          onClick={() => nav("/login")}
          className="absolute top-6 left-6 flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
        >
          <Home className="w-5 h-5" />
          <span className="font-medium">로그인으로</span>
        </button>

        <div className="w-full max-w-5xl mx-auto flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-md">
            <div className="bg-gray-900/50 backdrop-blur-xl border border-gray-800 rounded-3xl p-8">
              {/* 로고 */}
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl">
                  <img src={Logo} alt="elva logo" className="w-20 h-20" />
                </div>
              </div>

              <h2 className="text-2xl font-bold text-white mb-2 text-center">
                비밀번호 찾기
              </h2>
              <p className="text-gray-400 mb-8 text-center">
                {step === 1 && "가입한 이메일 주소를 입력해주세요"}
                {step === 2 && "이메일로 전송된 인증코드를 입력해주세요"}
                {step === 3 && "새로운 비밀번호를 설정해주세요"}
              </p>

              {/* Step Indicator */}
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= 1 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  >
                    <span className="text-sm font-semibold">1</span>
                  </div>
                  <div
                    className={`w-12 h-1 ${
                      step >= 2 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  ></div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= 2 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  >
                    <span className="text-sm font-semibold">2</span>
                  </div>
                  <div
                    className={`w-12 h-1 ${
                      step >= 3 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  ></div>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= 3 ? "bg-blue-600" : "bg-gray-700"
                    }`}
                  >
                    <span className="text-sm font-semibold">3</span>
                  </div>
                </div>
              </div>

              {/* Step 1: 이메일 입력 */}
              {step === 1 && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    sendVerificationCode();
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      이메일 주소
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="example@email.com"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        전송 중...
                      </>
                    ) : (
                      <>
                        인증코드 받기
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              )}

              {/* Step 2: 인증코드 입력 */}
              {step === 2 && (
                <div className="space-y-6">
                  {/* 타이머 */}
                  <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                    <p className="text-gray-400 text-sm mb-2">남은 시간</p>
                    <p
                      className={`text-2xl font-bold ${
                        timeLeft < 60 ? "text-red-400" : "text-blue-400"
                      }`}
                    >
                      {formatTime(timeLeft)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      인증코드
                    </label>
                    <input
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all text-center text-xl tracking-widest"
                      placeholder="6자리 코드"
                      maxLength="6"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={verifyCode}
                    disabled={!inputCode || timeLeft === 0}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    인증코드 확인
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <div className="flex flex-col gap-3">
                    <button
                      onClick={resendCode}
                      disabled={isLoading || timeLeft > 1620} // 27분 이상 남았을 때는 재전송 불가
                      className="w-full bg-gray-800/50 hover:bg-gray-800 text-gray-300 py-2 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      인증코드 재전송
                    </button>

                    <p className="text-center text-gray-500 text-sm">
                      인증코드를 받지 못하셨나요?
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: 새 비밀번호 설정 */}
              {step === 3 && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    resetPassword();
                  }}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      새 비밀번호
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="새 비밀번호 (8자 이상)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      새 비밀번호 확인
                    </label>
                    <div className="relative">
                      <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:bg-gray-800 transition-all"
                        placeholder="새 비밀번호를 다시 입력하세요"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-400" />
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full group bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        변경 중...
                      </>
                    ) : (
                      <>
                        비밀번호 변경하기
                        <KeyRound className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              )}

              <div className="mt-8 pt-8 border-t border-gray-800 text-center">
                <p className="text-gray-400">
                  비밀번호가 기억나셨나요?{" "}
                  <button
                    className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                    onClick={() => nav("/login")}
                  >
                    로그인하기
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toast open={showToast} text={toastMessage} onDone={handleToastDone} />

      <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
